/* eslint-disable */
// Polyfill for requestAnimationFrame for older browsers
if (!window.requestAnimationFrame) {
  window.requestAnimationFrame =
    window.mozRequestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.msRequestAnimationFrame ||
    function (callback) {
      setTimeout(callback, 0);
    };
}

import { normalizePlausibleObject } from './plausibleNormalizer.js';

var innerObject = {
  debug: window.location.host !== "app.ganttpro.com", // Debug mode for non-production environments

  init: function () {
    var that = this;

    // Initialize Amplitude with GDPR-compliant settings
    amplitude.getInstance().init(GT.amplitudeApiToken, null, {
      includeUtm: false, // Do not track UTM parameters
      includeReferrer: false, // Do not track referrer data
      disableCookies: true, // Avoid setting cookies to minimize data collection
      trackingOptions: {
        ip_address: false, // Do not track user IP addresses
        city: false, // Do not collect city information
        country: false, // Do not collect country information
        region: false, // Do not collect region information
      },
    });

    // Bugsnag configuration for error reporting
    if (window.Bugsnag && GT.bugsnagApiToken && GT.bugsnagApiToken.client) {
      var bugSnagClientConfig = {
        apiKey: GT.bugsnagApiToken.client,
        releaseStage: "production",
        autoTrackSessions: false, // Disable session tracking by default
        sendCode: false, // Avoid sending source code data
        appVersion: GT.buildVersion, // App version for debugging
      };

      Bugsnag.start(bugSnagClientConfig);

      // Add additional metadata for debugging in Bugsnag
      if (that.debug && window.GT) {
        Bugsnag.addMetadata(window.GT);
      }
    } else {
      console.warn("Bugsnag is not initialized. Only available on ganttpro.com.");
    }

    that.isEnabled = true;

    if (window.user && window.user.id) {
      const extractUrlParts = (url) => {
        try {
          const myUrl = new URL(url);
          return {
            base: myUrl.protocol + "//" + myUrl.hostname + myUrl.pathname.split("?")[0].split("&")[0].split("#")[0],
            path: myUrl.pathname.split("?")[0].split("&")[0].split("#")[0],
            domain: myUrl.hostname,
          };
        } catch (e) {
          return {
            base: null,
            path: null,
            domain: null,
          };
        }
      };

      let userClone = Object.assign({}, user);

      let gp_landing = extractUrlParts(userClone.settings.gp_landing);
      let gp_regsource = extractUrlParts(userClone.settings.gp_regsource);
      let gp_referrer = extractUrlParts(userClone.settings.gp_referrer);

      let analyticsUser = {
        id: userClone.id, // User ID (necessary for identifying sessions and linking data)
        locale: userClone.locale, // User language preference (non-personal)
        email: userClone.email, // User email 
        registrationTime: userClone.registrationTime, // Registration timestamp (non-personal)
        loginMethod: userClone.loginMethod, // Login method (e.g., "Google", "Email")

        settings: {
          country: userClone.settings.country, // Country-level location (not considered personal)
          gp_landing: userClone.settings.gp_landing, // Landing page (non-personal)
          gp_referrer: userClone.settings.gp_referrer, // Referrer (non-personal)
          gp_regsource: userClone.settings.gp_regsource, // Registration source (non-personal)
          gp_landing_base: gp_landing.base, // Landing page base URL (non-personal)
          gp_landing_path: gp_landing.path, // Landing page path (non-personal)
          gp_landing_domain: gp_landing.domain, // Landing page domain (non-personal)

          gp_regsource_base: gp_regsource.base,
          gp_regsource_path: gp_regsource.path,
          gp_regsource_domain: gp_regsource.domain,

          gp_referrer_base: gp_referrer.base,
          gp_referrer_path: gp_referrer.path,
          gp_referrer_domain: gp_referrer.domain,

          gp_utm: userClone.settings.gp_utm, // UTM parameters (aggregate-level tracking)
          user_company: userClone.settings.user_company, // Company name (contextual, not personal)
          user_industry: userClone.settings.user_industry, // Industry of the user (contextual)
          user_job_field: userClone.settings.user_job_field, // User's job field (non-personal)
          user_tool_used_before: userClone.settings.user_tool_used_before, // Tool usage info (non-personal)
          user_planning_use_for: userClone.settings.user_planning_use_for, // Use case (non-personal)
          user_interested_features: userClone.settings.user_interested_features, // Interested features (non-personal)
          user_job_title: userClone.settings.user_job_title, // Job title (contextual, not personal)
          user_team: userClone.settings.user_team, // Team information (contextual)

          onboarding_is_finished: userClone.settings.onboarding_is_finished, // Onboarding status
          user_scoring_percent: userClone.settings.user_scoring_percent, // Scoring percentage
          user_scoring_value: userClone.settings.user_scoring_value, // Scoring value
          ad_source: userClone.settings.ad_source, // Advertisement source
        },

        team: {
          id: userClone.team.id, // Team ID
          is_owner: userClone.team.is_owner, // Ownership status
          owner_email: userClone.team.owner_email,
          owner_id: userClone.team.owner_id, // Team owner's user ID
          is_jira: userClone.team.is_jira, // Jira integration status
        },

        subscription: {
          date_to: userClone.subscription.date_to, // Subscription end date
          is_annual: userClone.subscription.is_annual, // Annual subscription status
          is_trial: userClone.subscription.is_trial, // Trial status
          payment_plans_id: userClone.subscription.payment_plans_id, // Payment plan ID
          provider: userClone.subscription.provider, // Payment provider
          user_count: userClone.subscription.user_count, // Number of users in subscription
        },

        sso: {
          enabled: userClone.sso?.enabled || false, // SSO enabled status
        },

        twoFa: {
          active: userClone.twoFa?.active || false, // 2FA enabled status
        },

        affiliate: {
          key: userClone?.affiliate?.key, // Affiliate key
          coupon: userClone?.affiliate?.coupon, // Affiliate coupon
        },
      };

      // Set user properties in Amplitude
      amplitude.getInstance().setUserId(userClone.id);
      amplitude.getInstance().setUserProperties(analyticsUser);

      if (that.debug) {
        console.info("Analytics User", analyticsUser);
      }
    }
  },

  log: function (str, obj) {
    var that = this;
    obj = obj || {};

    if (!str) {
      return console.error("userExtAnalytics: log: nothing to log");
    }

    if (!that.isEnabled) {
      return;
    }

    if (that.debug) {
      console.info("%c LOG: " + str, "color: #bada55", obj);
    }

    // Log the event in various analytics tools
    return requestAnimationFrame(function () {
      // Log event in Amplitude
      amplitude.getInstance().logEvent(str, obj);

      // Optionally, log the same event in Google Analytics
      if (window.dataLayer && window.dataLayer.push) {
        window.dataLayer.push({
          event: str,
          eventCategory: str,
          eventValue: str,
          eventAction: str,
          eventLabel: str,
        });

        if (that.debug) {
          console.log("Event logged to GA:", str);
        }
      } else if (that.debug) {
        console.log("No dataLayer found for GA logging.");
      }
      // Log to Plausible
      if (window.plausible) {
        try {
          // Use normalized object for Plausible
          const normalizedObj = normalizePlausibleObject(obj);
          
          if (that.debug) {
            console.log("Event logged to Plausible:", str, normalizedObj, 'normalized');
          }
          
          plausible(str, {
            props: normalizedObj,
          });
        } catch (error) {
          console.error('Error logging plausible event:', error);
        }
      }
    });
  },

  on: function () {
    this.isEnabled = true; // Enable logging
  },

  off: function () {
    this.isEnabled = false; // Disable logging
  },

  initHotJarAndStartRecording: function () {
    // Hotjar initialization is triggered only if user consents to tracking
    if (window.user) {
      (function (h, o, t, j, a, r) {
        h.hj =
          h.hj ||
          function () {
            (h.hj.q = h.hj.q || []).push(arguments);
          };
        h._hjSettings = { hjid: 2690383, hjsv: 6 };
        a = o.getElementsByTagName("head")[0];
        r = o.createElement("script");
        r.async = 1;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a.appendChild(r);
      })(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");

      hj("trigger", "startRecording");
    } 
  },
};

export default innerObject;